/**
 * Iniciar atendimento com um cliente que nunca escreveu (C31).
 *
 * ── Por que o formulário tem essa ordem ───────────────────────────────────
 *
 * Número, depois modelo, depois as variáveis. Não é preferência: os modelos
 * são aprovados POR NÚMERO na Meta, então perguntar o modelo antes obrigaria a
 * refazer a escolha ao trocar de número. E as variáveis só existem depois de
 * haver modelo — cada um tem o seu número delas.
 *
 * ── O corpo é mostrado com os valores preenchidos ─────────────────────────
 *
 * O modelo cru — "Olá {{1}}, tudo bem?" — não é o que o cliente vai ler, e
 * quem manda precisa ver o que sai. A prévia substitui `{{n}}` pelo que foi
 * digitado, e deixa o `{{n}}` à vista quando o campo está vazio: assim a falta
 * aparece como falta, e não como texto estranho.
 *
 * ── Um número só não vira pergunta ────────────────────────────────────────
 *
 * Com um único número conectado, ele vem escolhido e o campo não aparece.
 * Perguntar "por qual número?" quando não há escolha é fazer a pessoa
 * confirmar o óbvio no meio de uma tarefa.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useCanaisDeWhatsApp, useModelosDeMensagem } from '@/shared/api/consultas';
import { Botao, Campo, CampoSelecao, Dialogo } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';

export interface PedidoDeAtendimento {
  readonly inbox_referencia_externa: string;
  readonly template: {
    readonly nome: string;
    readonly idioma: string;
    readonly valores: readonly string[];
  };
}

/** Troca `{{1}}`, `{{2}}`… pelo que foi digitado. Vazio permanece `{{n}}`. */
export function comValores(corpo: string, valores: readonly string[]): string {
  return corpo.replace(/\{\{(\d+)\}\}/g, (marca, n: string) => {
    const valor = valores[Number(n) - 1];
    return valor && valor.trim() !== '' ? valor : marca;
  });
}

export function DialogoDeIniciarAtendimento({
  cliente,
  salvando,
  erro,
  aoEnviar,
  aoFechar,
}: {
  cliente: { readonly nome: string | null; readonly telefone: string | null };
  salvando: boolean;
  erro?: string | null;
  aoEnviar: (pedido: PedidoDeAtendimento) => void;
  aoFechar: () => void;
}): ReactNode {
  const canais = useCanaisDeWhatsApp();
  const itens = canais.data?.itens ?? [];

  const [inbox, definirInbox] = useState('');
  const escolhido = inbox || (itens.length === 1 ? itens[0]!.referencia_externa : '');

  const modelos = useModelosDeMensagem(escolhido);
  const aprovados = useMemo(
    () => (modelos.data?.itens ?? []).filter((m) => m.status === 'APPROVED'),
    [modelos.data],
  );

  const [nomeDoModelo, definirNomeDoModelo] = useState('');
  const modelo = aprovados.find((m) => `${m.nome}|${m.idioma}` === nomeDoModelo) ?? null;
  const [valores, definirValores] = useState<string[]>([]);

  /*
   * Trocar de modelo zera as variáveis.
   *
   * Sem isto, o valor digitado para a primeira variável de um modelo ficaria na
   * primeira variável do outro — que quer dizer outra coisa. O cliente
   * receberia a mensagem certa com o dado errado, que é pior que erro visível.
   */
  useEffect(() => {
    definirValores(Array.from({ length: modelo?.variaveis ?? 0 }, () => ''));
  }, [modelo?.nome, modelo?.idioma, modelo?.variaveis]);

  const faltaValor = valores.some((v) => v.trim() === '');
  const podeEnviar = !!modelo && !faltaValor && !salvando && !!cliente.telefone;

  return (
    <Dialogo
      titulo="Iniciar atendimento"
      forma="lateral"
      aoFechar={aoFechar}
      botoes={
        <>
          <Botao
            onClick={aoFechar}
            desabilitado={salvando}
            motivo="Aguarde: a mensagem está saindo."
          >
            Cancelar
          </Botao>
          <Botao
            type="submit"
            form="iniciar-atendimento"
            variante="primario"
            desabilitado={!podeEnviar}
            motivo={
              !cliente.telefone
                ? 'Este cliente não tem telefone cadastrado.'
                : salvando
                  ? 'Enviando…'
                  : !modelo
                    ? 'Escolha o modelo aprovado que vai ser enviado.'
                    : 'Preencha todas as variáveis do modelo.'
            }
          >
            {salvando ? 'Enviando…' : 'Enviar e abrir conversa'}
          </Botao>
        </>
      }
    >
      {erro != null && (
        <p className="dialogo__erro" role="alert">
          {erro}
        </p>
      )}

      {/*
        A regra do canal, dita ANTES de a pessoa escolher.
        Ela explica por que não há campo de texto livre aqui — e sem a
        explicação, a ausência do campo parece limitação do CliqCRM.
      */}
      <Faixa tom="atencao">
        Quem nunca escreveu só recebe <strong>modelo aprovado</strong> — é regra do WhatsApp, não
        nossa. A conversa abre quando a mensagem sai, e a resposta dele cai na fila.
      </Faixa>

      {!cliente.telefone && (
        <Faixa tom="bloqueado">
          <strong>{cliente.nome ?? 'Este cliente'}</strong> não tem telefone cadastrado. Edite o
          cadastro antes de iniciar o atendimento.
        </Faixa>
      )}

      <form
        id="iniciar-atendimento"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (!podeEnviar || !modelo) return;
          aoEnviar({
            inbox_referencia_externa: escolhido,
            template: { nome: modelo.nome, idioma: modelo.idioma, valores },
          });
        }}
      >
        {itens.length > 1 && (
          <CampoSelecao
            rotulo="Falar por qual número"
            value={escolhido}
            onChange={(evento) => definirInbox(evento.target.value)}
          >
            <option value="">Escolha o número</option>
            {itens.map((canal) => (
              <option key={canal.referencia_externa} value={canal.referencia_externa}>
                {canal.nome} — {canal.telefone}
              </option>
            ))}
          </CampoSelecao>
        )}

        {escolhido && (
          <CampoSelecao
            rotulo="Modelo aprovado"
            value={nomeDoModelo}
            ajuda={
              modelos.isPending
                ? 'Buscando os modelos deste número…'
                : aprovados.length === 0
                  ? 'Nenhum modelo aprovado neste número. Cadastre em Configurações → Modelos.'
                  : undefined
            }
            onChange={(evento) => definirNomeDoModelo(evento.target.value)}
          >
            <option value="">Escolha o modelo</option>
            {aprovados.map((m) => (
              <option key={`${m.nome}|${m.idioma}`} value={`${m.nome}|${m.idioma}`}>
                {m.nome} ({m.idioma})
              </option>
            ))}
          </CampoSelecao>
        )}

        {modelo &&
          valores.map((valor, indice) => (
            <Campo
              key={`${modelo.nome}-${indice}`}
              rotulo={`Variável {{${indice + 1}}}`}
              type="text"
              value={valor}
              maxLength={200}
              onChange={(evento) =>
                definirValores((atuais) =>
                  atuais.map((v, i) => (i === indice ? evento.target.value : v)),
                )
              }
            />
          ))}

        {modelo && (
          /*
            A prévia é o que o cliente vai ler. Ela existe porque o corpo cru
            com `{{1}}` não permite conferir concordância nem pontuação — e uma
            mensagem de primeiro contato é a primeira impressão da revenda.
          */
          <p className="iniciar-atendimento__previa">
            <span className="iniciar-atendimento__previa-rotulo">O cliente vai receber</span>
            {comValores(modelo.corpo, valores)}
            {modelo.rodape && <span className="iniciar-atendimento__rodape">{modelo.rodape}</span>}
          </p>
        )}
      </form>
    </Dialogo>
  );
}
